
// GA4 Tracking Utility for D2C-Chatbot-Boost
// Implements Automotive Standards Council (ASC) compliant events
// Vendor: D2C Media

import { logger } from './logger';

// Extend the global Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

// ASC Standard Automotive Events
export enum ASCEvents {
  VEHICLE_VIEW = 'vehicle_view',
  LEAD_GENERATION = 'lead_generation',
  SCHEDULE_SERVICE = 'schedule_service',
  REQUEST_QUOTE = 'request_quote',
  CARFAX_REQUEST = 'carfax_request',
  FINANCING_REQUEST = 'financing_request',
  TRADE_ESTIMATE = 'trade_estimate',
  APPOINTMENT_SCHEDULE = 'appointment_schedule',
  CONTACT_DEALER = 'contact_dealer',
  SIMILAR_VEHICLES = 'similar_vehicles_view',
  CHAT_INTERACTION = 'chat_interaction',
  FORM_START = 'form_start',
  FORM_COMPLETE = 'form_complete'
}

// Vehicle data structure for tracking
interface VehicleData {
  vehicle_id?: string;
  vehicle_vin?: string;
  vehicle_stock?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_year?: string | number;
  vehicle_trim?: string;
  vehicle_color?: string;
  vehicle_price?: number;
  vehicle_mileage?: number | string;
  vehicle_type?: 'new' | 'used';
}

// Lead data structure
interface LeadData {
  lead_type: string;
  contact_method: string;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  interest_vehicle?: VehicleData;
}

// Base GA4 event structure
interface GA4Event {
  event: string;
  event_category?: string;
  event_label?: string;
  event_value?: number;
  custom_parameters?: Record<string, any>;
}

class GA4Tracker {
  private isInitialized = false;
  private readonly toolName = 'D2C-Chatbot-Boost';
  private readonly vendor = 'D2C Media';

  constructor() {
    this.initialize();
  }

  private initialize() {
    // Check if GA4 is available
    if (typeof window !== 'undefined') {
      // Initialize dataLayer if it doesn't exist
      if (!window.dataLayer) {
        window.dataLayer = [];
      }
      this.isInitialized = true;
      logger.log('GA4 Tracker initialized for', this.toolName, 'by', this.vendor);
    }
  }

  private pushEvent(eventData: GA4Event) {
    if (!this.isInitialized) {
      logger.warn('GA4 Tracker not initialized');
      return;
    }

    try {
      // Add tool and vendor information to all events
      const enrichedEvent = {
        ...eventData,
        tool_name: this.toolName,
        tool_vendor: this.vendor,
        event_source: 'chatbot_widget',
        ...eventData.custom_parameters
      };

      // Push to dataLayer
      if (window.dataLayer) {
        window.dataLayer.push(enrichedEvent);
        logger.log('GA4 Event pushed:', enrichedEvent);
      }

      // Also try gtag if available
      if (window.gtag) {
        window.gtag('event', eventData.event, enrichedEvent);
      }
    } catch (error) {
      logger.error('Failed to push GA4 event:', error);
    }
  }

  // ASC Standard: Vehicle View Event
  trackVehicleView(vehicleData: VehicleData) {
    this.pushEvent({
      event: ASCEvents.VEHICLE_VIEW,
      event_category: 'automotive',
      event_label: `${vehicleData.vehicle_make} ${vehicleData.vehicle_model}`,
      custom_parameters: {
        ...vehicleData,
        interaction_type: 'view_details'
      }
    });
  }

  // ASC Standard: Lead Generation Event
  trackLeadGeneration(leadData: LeadData) {
    this.pushEvent({
      event: ASCEvents.LEAD_GENERATION,
      event_category: 'automotive',
      event_label: leadData.lead_type,
      custom_parameters: {
        lead_type: leadData.lead_type,
        contact_method: leadData.contact_method,
        has_vehicle_interest: !!leadData.interest_vehicle,
        ...leadData.interest_vehicle
      }
    });
  }

  // ASC Standard: Service Scheduling
  trackServiceSchedule(serviceData: { service_type?: string; appointment_date?: string; vehicle?: VehicleData }) {
    this.pushEvent({
      event: ASCEvents.SCHEDULE_SERVICE,
      event_category: 'automotive',
      event_label: serviceData.service_type || 'general_service',
      custom_parameters: {
        service_type: serviceData.service_type,
        appointment_date: serviceData.appointment_date,
        ...serviceData.vehicle
      }
    });
  }

  // ASC Standard: Quote Request
  trackQuoteRequest(quoteData: { quote_type: string; vehicle?: VehicleData }) {
    this.pushEvent({
      event: ASCEvents.REQUEST_QUOTE,
      event_category: 'automotive',
      event_label: quoteData.quote_type,
      custom_parameters: {
        quote_type: quoteData.quote_type,
        ...quoteData.vehicle
      }
    });
  }

  // ASC Standard: CARFAX Request
  trackCarfaxRequest(vehicleData: VehicleData) {
    this.pushEvent({
      event: ASCEvents.CARFAX_REQUEST,
      event_category: 'automotive',
      event_label: vehicleData.vehicle_vin || 'unknown_vin',
      custom_parameters: {
        ...vehicleData,
        report_type: 'carfax'
      }
    });
  }

  // ASC Standard: Financing Request
  trackFinancingRequest(financingData: { application_type?: string; vehicle?: VehicleData }) {
    this.pushEvent({
      event: ASCEvents.FINANCING_REQUEST,
      event_category: 'automotive',
      event_label: financingData.application_type || 'credit_application',
      custom_parameters: {
        application_type: financingData.application_type,
        ...financingData.vehicle
      }
    });
  }

  // ASC Standard: Trade Estimate
  trackTradeEstimate(tradeData: { vehicle?: VehicleData; interest_vehicle?: VehicleData }) {
    this.pushEvent({
      event: ASCEvents.TRADE_ESTIMATE,
      event_category: 'automotive',
      event_label: 'trade_valuation',
      custom_parameters: {
        trade_vehicle: tradeData.vehicle,
        interest_vehicle: tradeData.interest_vehicle
      }
    });
  }

  // ASC Standard: Appointment Scheduling
  trackAppointmentSchedule(appointmentData: { appointment_type?: string; date?: string; vehicle?: VehicleData }) {
    this.pushEvent({
      event: ASCEvents.APPOINTMENT_SCHEDULE,
      event_category: 'automotive',
      event_label: appointmentData.appointment_type || 'general_appointment',
      custom_parameters: {
        appointment_type: appointmentData.appointment_type,
        appointment_date: appointmentData.date,
        ...appointmentData.vehicle
      }
    });
  }

  // ASC Standard: Contact Dealer
  trackContactDealer(contactData: { contact_type: string; vehicle?: VehicleData }) {
    this.pushEvent({
      event: ASCEvents.CONTACT_DEALER,
      event_category: 'automotive',
      event_label: contactData.contact_type,
      custom_parameters: {
        contact_type: contactData.contact_type,
        ...contactData.vehicle
      }
    });
  }

  // Similar Vehicles View
  trackSimilarVehiclesView(vehicleData: VehicleData) {
    this.pushEvent({
      event: ASCEvents.SIMILAR_VEHICLES,
      event_category: 'automotive',
      event_label: 'similar_models',
      custom_parameters: {
        ...vehicleData,
        interaction_type: 'similar_vehicles'
      }
    });
  }

  // Chat Interaction Tracking
  trackChatInteraction(interactionData: { interaction_type: string; message_type?: string; form_type?: string }) {
    this.pushEvent({
      event: ASCEvents.CHAT_INTERACTION,
      event_category: 'engagement',
      event_label: interactionData.interaction_type,
      custom_parameters: {
        interaction_type: interactionData.interaction_type,
        message_type: interactionData.message_type,
        form_type: interactionData.form_type
      }
    });
  }

  // Form Start Event
  trackFormStart(formType: string) {
    this.pushEvent({
      event: ASCEvents.FORM_START,
      event_category: 'form_interaction',
      event_label: formType,
      custom_parameters: {
        form_type: formType
      }
    });
  }

  // Form Complete Event
  trackFormComplete(formType: string, vehicleData?: VehicleData) {
    this.pushEvent({
      event: ASCEvents.FORM_COMPLETE,
      event_category: 'form_interaction',
      event_label: formType,
      custom_parameters: {
        form_type: formType,
        ...vehicleData
      }
    });
  }

  // D2C Chatbot Specific Events
  trackChatbotImpression() {
    this.pushEvent({
      event: 'd2chatbot-impressions',
      event_category: 'chatbot_engagement',
      event_label: 'collapsed_icon_impression',
      custom_parameters: {
        widget_state: 'collapsed'
      }
    });
  }

  trackChatbotClick() {
    this.pushEvent({
      event: 'd2chatbot-clicks',
      event_category: 'chatbot_engagement',
      event_label: 'collapsed_icon_click',
      custom_parameters: {
        widget_state: 'collapsed'
      }
    });
  }

  trackChatStarted() {
    this.pushEvent({
      event: 'd2chatbot-chat-started',
      event_category: 'chatbot_engagement',
      event_label: 'chat_expanded',
      custom_parameters: {
        widget_state: 'expanded'
      }
    });
  }

  trackUserMessageSent(messageLength?: number) {
    this.pushEvent({
      event: 'd2chatbot-usermessage-sent',
      event_category: 'chatbot_interaction',
      event_label: 'user_message',
      custom_parameters: {
        message_length: messageLength
      }
    });
  }

  trackAgentMessageSent(messageLength?: number) {
    this.pushEvent({
      event: 'd2chatbot-agentmessage-sent',
      event_category: 'chatbot_interaction',
      event_label: 'agent_response',
      custom_parameters: {
        message_length: messageLength
      }
    });
  }

  trackLeadSent(leadType: string, leadData?: any) {
    this.pushEvent({
      event: 'd2chatbot-lead-sent',
      event_category: 'chatbot_conversion',
      event_label: leadType,
      custom_parameters: {
        lead_type: leadType,
        has_contact_info: !!(leadData?.name || leadData?.email || leadData?.phone),
        form_type: leadData?.formType
      }
    });
  }

  // Google Ads Conversion Tracking
  trackGoogleAdsConversion(conversionId: string, conversionLabel: string) {
    if (!conversionId || !conversionLabel) {
      logger.log('Google Ads conversion skipped: missing ID or label');
      return;
    }

    try {
      // Use gtag if available
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': `AW-${conversionId}/${conversionLabel}`,
          'value': 1.0,
          'currency': 'CAD'
        });
        logger.log('✅ Google Ads conversion fired via gtag:', `AW-${conversionId}/${conversionLabel}`, 'value: 1.0 CAD');
      } else {
        // Fallback: push to dataLayer for GTM
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'conversion',
            send_to: `AW-${conversionId}/${conversionLabel}`,
            tool_name: this.toolName,
            tool_vendor: this.vendor
          });
          logger.log('✅ Google Ads conversion pushed to dataLayer:', `AW-${conversionId}/${conversionLabel}`);
        }
      }
    } catch (error) {
      logger.error('Failed to fire Google Ads conversion:', error);
    }
  }
}

// Export singleton instance
export const ga4Tracker = new GA4Tracker();

// Helper function to extract vehicle data from various sources
export const extractVehicleData = (vehicle: any): VehicleData => {
  return {
    vehicle_vin: vehicle?.VIN || vehicle?.vin,
    vehicle_stock: vehicle?.Stock_Number || vehicle?.stock,
    vehicle_make: vehicle?.Make || vehicle?.make,
    vehicle_model: vehicle?.Model || vehicle?.model,
    vehicle_year: vehicle?.Year || vehicle?.year,
    vehicle_trim: vehicle?.Trim || vehicle?.version,
    vehicle_color: vehicle?.["Exterior Color"] || vehicle?.color,
    vehicle_price: vehicle?.Price || vehicle?.price,
    vehicle_mileage: vehicle?.Mileage || vehicle?.mileage,
    vehicle_type: (vehicle?.Mileage && vehicle.Mileage < 50) ? 'new' : 'used'
  };
};
